/**
 * Layout utilities for React Flow auto-arrangement using Dagre
 *
 * Dagre is the recommended library for hierarchical graph layouts.
 * It automatically handles:
 * - Node depth/rank calculation
 * - Parallel branches side-by-side
 * - Edge routing optimization
 * - Consistent spacing
 */

import Dagre from '@dagrejs/dagre';
import { Node, Edge } from 'reactflow';

export interface LayoutOptions {
  direction?: 'TB' | 'BT' | 'LR' | 'RL'; // Top-Bottom, Bottom-Top, Left-Right, Right-Left
  nodeWidth?: number;
  nodeHeight?: number;
  nodeSep?: number; // Horizontal spacing between nodes
  rankSep?: number; // Vertical spacing between ranks/rows
  edgeSep?: number; // Minimum separation between edges
  ranker?: 'network-simplex' | 'tight-tree' | 'longest-path';
}

/**
 * Auto-layout nodes using Dagre algorithm
 *
 * Dagre creates a hierarchical layout where:
 * - Nodes are organized by "rank" (depth from root)
 * - Nodes at same rank appear in the same row
 * - Parallel branches are placed side-by-side
 * - Edges are routed to minimize crossings
 */
export function getLayoutedElements(
  nodes: Node[],
  edges: Edge[],
  options: LayoutOptions = {}
): { nodes: Node[]; edges: Edge[] } {
  const {
    direction = 'TB',
    nodeWidth = 250,
    nodeHeight = 80,
    nodeSep = 50,    // Horizontal gap between nodes
    rankSep = 100,   // Vertical gap between rows
    ranker = 'network-simplex',
  } = options;

  if (nodes.length === 0) {
    return { nodes, edges };
  }

  // Create a new directed graph
  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));

  // Set graph options
  g.setGraph({
    rankdir: direction,
    nodesep: nodeSep,
    ranksep: rankSep,
    ranker: ranker,
    marginx: 50,
    marginy: 50,
  });

  // Add nodes to the graph
  nodes.forEach((node) => {
    // Use actual node dimensions if available, otherwise use defaults
    const width = node.width || nodeWidth;
    const height = node.height || nodeHeight;
    g.setNode(node.id, { width, height });
  });

  // Add edges to the graph
  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target);
  });

  // Run the dagre layout algorithm
  Dagre.layout(g);

  // Map the computed positions back to React Flow nodes
  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = g.node(node.id);

    // Dagre returns center positions, React Flow uses top-left
    // Adjust by subtracting half the width/height
    const width = node.width || nodeWidth;
    const height = node.height || nodeHeight;

    return {
      ...node,
      position: {
        x: nodeWithPosition.x - width / 2,
        y: nodeWithPosition.y - height / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
}
